<template>
    <div class="m-grid__item m-grid__item--fluid m-wrapper">
        <div class="m-subheader">
            <div class="d-flex align-items-center">
                <div class="mr-auto">
                    <h3 class="m-subheader__title m-subheader__title--separator">
                        Badges
                    </h3>
                    <ul class="m-subheader__breadcrumbs m-nav m-nav--inline">
                        <li class="m-nav__item m-nav__item--home">
                            <router-link exact :to="{ name: 'home' }" class="m-nav__link m-nav__link--icon">
                                <i class="m-nav__link-icon la la-home"></i>
                            </router-link>
                        </li>
                        <li class="m-nav__separator">-</li>
                        <li class="m-nav__item">
                            <router-link exact :to="{ name : 'challenge' }" class="m-nav__link">
                                <span class="m-nav__link-text">Badges</span>
                            </router-link>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="m-content">
            <div class="m-portlet m-portlet--mobile">
                <div class="m-portlet__head">
                    <div class="m-portlet__head-caption">
                        <div class="m-portlet__head-title">
                            <h3 class="m-portlet__head-text">
                                Badges (Challenges)
                                <small>Mileage-based achievements shown in the mobile app</small>
                            </h3>
                        </div>
                    </div>
                </div>
                <div class="m-portlet__body">
                    <!-- Search + Add bar -->
                    <div class="m-form m-form--label-align-right m--margin-top-20 m--margin-bottom-30">
                        <div class="row align-items-center">
                            <div class="col-xl-8 order-2 order-xl-1">
                                <div class="form-group m-form__group row align-items-center">
                                    <div class="col-md-4">
                                        <div class="m-input-icon m-input-icon--left">
                                            <input type="text"
                                                   class="form-control m-input m-input--solid"
                                                   v-model="search"
                                                   placeholder="Search by name..."
                                                   id="generalSearch">
                                            <span class="m-input-icon__icon m-input-icon__icon--left">
                                                <span><i class="la la-search"></i></span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-xl-4 order-1 order-xl-2 m--align-right">
                                <button v-b-modal="'challengeModal'"
                                        class="btn btn-brand m-btn m-btn--custom m-btn--icon m-btn--air m-btn--pill">
                                    <span>
                                        <i class="la la-plus"></i>
                                        <span>New Badge</span>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="alert alert-success alert-dismissible" role="alert" v-if="alertCreated">
                        <button type="button" class="close" aria-label="Close" @click="alertCreated=false"></button>
                        New badge created successfully
                    </div>
                    <div class="alert alert-success alert-dismissible" role="alert" v-if="alertUpdated">
                        <button type="button" class="close" aria-label="Close" @click="alertUpdated=false"></button>
                        Badge updated successfully
                    </div>

                    <div class="table-responsive">
                        <table class="table table-inverse" v-if="challenges.length > 0">
                            <thead>
                            <tr>
                                <th>#</th>
                                <th>Icon</th>
                                <th>Name</th>
                                <th>Required miles</th>
                                <th>Description</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(challenge, index) in filteredChallenges" :key="challenge.id">
                                <td>{{ index + 1 }}</td>
                                <td>
                                    <gallery-preview v-if="parsedIcon(challenge.badge_icon)"
                                                     v-bind:gallery="parsedIcon(challenge.badge_icon)" />
                                    <span v-else class="text-muted">—</span>
                                </td>
                                <td>{{ challenge.name }}</td>
                                <td>{{ challenge.required_distance_miles }}</td>
                                <td class="text-muted">{{ challenge.description }}</td>
                                <td style="width: 100px;">
                                    <button title="Edit"
                                            class="btn m-btn m-btn--hover-brand m-btn--icon m-btn--icon-only m-btn--pill"
                                            v-b-modal="'challengeModal'"
                                            @click="updateChallenge(challenge.id)">
                                        <i class="la la-edit"></i>
                                    </button>
                                    <button title="Delete" @click="deleteItem(challenge.id, challenge.name)"
                                            class="btn m-btn m-btn--hover-danger m-btn--icon m-btn--icon-only m-btn--pill">
                                        <i class="la la-trash"></i>
                                    </button>
                                </td>
                            </tr>
                            </tbody>
                        </table>
                        <p v-else class="text-center text-muted m-3">No badges yet.</p>
                    </div>

                    <b-pagination v-if="total > 0 && total > pageSize" align="right" size="md"
                                  :total-rows="total" v-model="page" :per-page="pageSize">
                    </b-pagination>
                </div>
            </div>

            <challenge-modal @clear-id="clearId"
                             @clicked-reload="getList"
                             @show-create-success-alert="showCreateSuccessAlert"
                             @show-update-success-alert="showUpdateSuccessAlert"
                             v-bind:challengeId="challengeId" />
        </div>
    </div>
</template>

<script>
    import ChallengeApi from '../../api/challenge';
    import ChallengeModal from './ChallengeModal';
    import GalleryPreview from "../property/PropertyPreview";

    export default {
        components: {
            ChallengeModal,
            GalleryPreview
        },
        name: "challenge",
        data() {
            return {
                error: false,
                alertCreated: false,
                alertUpdated: false,
                message: null,
                page: 1,
                total: 0,
                search: '',
                pageSize: 15,
                challengeId: null,
                challenges: [],
            }
        },
        computed: {
            // Client-side filter on top of server-side pagination (mirrors other admin pages).
            filteredChallenges() {
                if (!this.search) return this.challenges;
                const q = this.search.toLowerCase();
                return this.challenges.filter(c => (c.name || '').toLowerCase().includes(q));
            }
        },
        watch: {
            page() { this.getList(); },
            pageSize() { this.getList(); }
        },
        beforeMount() {
            this.getList();
        },
        methods: {
            getList() {
                ChallengeApi.getChallenge(this, {
                    page: this.page,
                    pageSize: this.pageSize
                }, () => {});
            },
            updateChallenge(id) {
                this.challengeId = id;
                this.$bvModal.show('challengeModal');
            },
            clearId() {
                this.challengeId = null;
                this.getList();
            },
            showCreateSuccessAlert() {
                this.alertCreated = true;
                this.alertUpdated = false;
            },
            showUpdateSuccessAlert() {
                this.alertCreated = false;
                this.alertUpdated = true;
            },
            deleteItem(id, name) {
                if (confirm(`Remove badge "${name}"?`)) {
                    ChallengeApi.removeChallenge(this, id, () => {
                        this.getList();
                        this.alertCreated = false;
                        this.alertUpdated = false;
                    });
                }
            },
            // badge_icon is stored as a plain string. GalleryPreview will resolve the
            // final URL as `apiUrl + '/assets' + path`. We mirror Android's
            // AchievementBadgesAdapter.buildBadgeIconUrl logic exactly: prepend a
            // single '/' if missing, then GalleryPreview adds the apiUrl + /assets.
            //   "/uploads/x.png" → apiUrl + /assets/uploads/x.png
            //   "5m.png"          → apiUrl + /assets/5m.png  (matches Android baseUrl/assets/5m.png)
            parsedIcon(raw) {
                if (!raw || typeof raw !== 'string') return null;
                if (raw.startsWith('http://') || raw.startsWith('https://')) return null;
                const path = raw.startsWith('/') ? raw : '/' + raw;
                return { path, thumbnail: path };
            }
        }
    }
</script>

<style scoped></style>
